/**
 * Helpers compartilhados de mutação otimista do Relacionamento.
 *
 * Espelham o padrão de `controller/shared/leads/leads.js` da PR de Leads
 * (#1100 — Evitar carregamento massivo de dados). Cada slice do store que
 * denormaliza o cliente em relacionamento (clients, clientsTasks,
 * relationshipKanban, monthlyRoutine) é atualizado via uma única mutação
 * walker (`PATCH_CLIENT_IN_RELATIONSHIP`).
 *
 * Refetch single-client é **slice-específico** — cada view chama somente
 * o helper da slice que ela exibe. Não há fan-out paralelo das 3 APIs;
 * isso seria desperdício pois apenas uma das visões (Clientes / Tarefas /
 * Kanban) está montada por vez (radio-group em
 * `view-crm-relationship-priorities.vue`).
 *
 *   - `atualizarClienteNoRelacionamentoOtimista` — FLUXO A (edição de
 *     campo editável no modal): patch in-place via PATCH_CLIENT_IN_RELATIONSHIP.
 *
 *   - `recarregarClienteEmClients`  — FLUXO B (visão Clientes / overview)
 *   - `recarregarClienteEmTasks`    — FLUXO B (visão Tarefas)
 *   - `recarregarClienteEmKanban`   — FLUXO B (visão Kanban)
 *
 * Cada helper de Fluxo B faz 1 (uma) request com `id_cliente_app` na query
 * e despacha o upsert seletivo da slice correspondente.
 */

import store from '@state/store'
import { getNumber } from '@utils/Format'
import { apiCrmClients } from '@modules/crm/api/apiCrmClients'
import { apiCrmClientsTasks } from '@modules/crm/api/apiCrmClientsTasks'
import { apiCrmRelationshipKanban } from '@modules/crm/api/apiCrmRelationshipKanban'

// Campos copiados 1:1 pro patch quando presentes no payload — sem
// transformação (ao contrário de pl_potencial, que passa por getNumber).
const SIMPLE_COPY_KEYS = [
  'email',
  'telefone',
  'codigo_corretora',
  'id_profissional_responsavel',
  'cpf',
  'tags',
  'foto_url',
  // spec-007 — badge de sugestões de IA aguardando curadoria (global-modals.vue
  // patcha isso após accept/decline no ia-suggestions-modal, sem refetch da tela).
  'possui_sugestao_ia',
  'qtd_sugestoes_ia',
]

// Campos editáveis que aparecem nos cards. `nome`/`nome_cliente` são aliases
// porque as slices denormalizam o cliente com chaves distintas.
//
// Guard !== undefined em TODAS as chaves: `clienteData` pode ser um patch
// parcial de 1 campo (ex: crm-edit-field-modal editando só `id_origem`) —
// sem o guard, uma chave ausente vira `undefined` no patch e apaga o valor
// já exibido no card (ex: nome sumindo ao editar só o telefone; tags
// ausentes apagando as do card; foto sumindo numa edição comum).
const buildCardPatch = (clienteData = {}) => {
  const patch = {}
  if (clienteData.nome_cliente !== undefined) {
    patch.nome = clienteData.nome_cliente
    patch.nome_cliente = clienteData.nome_cliente
  }
  SIMPLE_COPY_KEYS.forEach((key) => {
    if (clienteData[key] !== undefined) patch[key] = clienteData[key]
  })
  if (clienteData.pl_potencial !== undefined) {
    patch.pl_potencial = getNumber(clienteData.pl_potencial)
  }
  return patch
}

export const atualizarClienteNoRelacionamentoOtimista = ({
  idClienteApp,
  clienteData,
} = {}) => {
  if (!idClienteApp || !clienteData || typeof clienteData !== 'object') return

  store.dispatch('crm/patchClientInRelationship', {
    idClienteApp,
    patch: buildCardPatch(clienteData),
  })
}

export const recarregarClienteEmClients = async ({ idClienteApp } = {}) => {
  if (!idClienteApp) {
    return { status: 400, message: 'Cliente inválido.' }
  }
  const r = await apiCrmClients.carregarCliente({ idClienteApp })
  if (r && r.status === 200 && r.value) {
    store.dispatch('crm/upsertClientInRelationshipFromResponse', {
      idClienteApp,
      responseData: r.value,
    })
  }
  return r
}

export const recarregarClienteEmTasks = async ({ idClienteApp } = {}) => {
  if (!idClienteApp) {
    return { status: 400, message: 'Cliente inválido.' }
  }
  const r = await apiCrmClientsTasks.carregarCliente({ idClienteApp })
  if (r && r.status === 200 && r.value) {
    store.dispatch('crm/upsertClientTasksInRelationshipFromResponse', {
      idClienteApp,
      responseData: r.value,
    })
  }
  return r
}

export const recarregarClienteEmKanban = async ({ idClienteApp } = {}) => {
  if (!idClienteApp) {
    return { status: 400, message: 'Cliente inválido.' }
  }
  const r = await apiCrmRelationshipKanban.carregarCliente({ idClienteApp })
  if (r && r.status === 200 && r.value) {
    store.dispatch('crm/upsertClientKanbanInRelationshipFromResponse', {
      idClienteApp,
      responseData: r.value,
    })
  }
  return r
}
